import React, { useDeferredValue } from 'react';
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Stack,
  Tooltip,
} from '@mui/material';
import { Appointment, AppointmentStatus } from '../../../types';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import EventRepeatOutlinedIcon from '@mui/icons-material/EventRepeatOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { EmptyState, LicensePlate, StatusChip, TableSkeleton } from '../../../components/ui';
import { isDeletableStatus } from './helpers';

interface AppointmentsTableProps {
  list: Appointment[];
  loading: boolean;
  formatAppointmentDateTime: (appointment: Appointment) => string;
  generatingInvoiceId: string | null;
  handleEditClick: (appointment: Appointment) => void;
  handleRescheduleClick: (appointment: Appointment) => void;
  handleDeleteAppointment: (id: string) => void;
  handleGenerateInvoice: (appointment: Appointment) => void;
}

/**
 * Rows in the first paint. Every row is a couple of dozen MUI components, and
 * the All view can hold hundreds, so the rest render in an interruptible
 * background pass instead of holding the screen change.
 */
const FIRST_PAINT_ROWS = 30;

/** The appointments table behind the All / Today / Week views. */
const AppointmentsTable: React.FC<AppointmentsTableProps> = ({
  list,
  loading,
  formatAppointmentDateTime,
  generatingInvoiceId,
  handleEditClick,
  handleRescheduleClick,
  handleDeleteAppointment,
  handleGenerateInvoice,
}) => {
  const source = useDeferredValue(
    list,
    list.length > FIRST_PAINT_ROWS ? list.slice(0, FIRST_PAINT_ROWS) : list,
  );
  return (
    <TableContainer sx={{ maxHeight: { md: 'calc(100vh - 320px)' } }}>
      <Table stickyHeader sx={{ minWidth: 900 }}>
        <TableHead>
          <TableRow>
            <TableCell>Customer</TableCell>
            <TableCell>Vehicle</TableCell>
            <TableCell>Date &amp; Time</TableCell>
            <TableCell>Service</TableCell>
            <TableCell>Comments</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Mechanic</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableSkeleton rows={6} columns={8} />
          ) : source.length > 0 ? (
            source.map((appointment) => (
              <TableRow key={appointment.id} hover>
                <TableCell>
                  <Typography variant="body2">{appointment.customerName}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {appointment.customerEmail}
                  </Typography>
                  <Typography variant="caption" display="block" color="text.secondary">
                    {appointment.customerPhone}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {appointment.vehicleModel} ({appointment.vehicleYear})
                  </Typography>
                  {appointment.licensePlate && <LicensePlate plate={appointment.licensePlate} />}
                </TableCell>
                <TableCell>
                  {formatAppointmentDateTime(appointment)}
                </TableCell>
                <TableCell>
                  <Typography
                    variant="body2"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {appointment.serviceDescription}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      fontStyle: 'italic',
                    }}
                  >
                    {appointment.comments || 'No comments'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <StatusChip status={appointment.status} />
                </TableCell>
                <TableCell>
                  {appointment.mechanic ? (
                    <>
                      <Typography variant="body2">{appointment.mechanic.user?.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {appointment.mechanic.specialization}
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="body2" color="text.disabled">
                      Not assigned
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={0.5} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => handleEditClick(appointment)}>
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Reschedule">
                      <IconButton size="small" onClick={() => handleRescheduleClick(appointment)}>
                        <EventRepeatOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {isDeletableStatus(appointment.status) && (
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteAppointment(appointment.id)}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {appointment.status === AppointmentStatus.COMPLETED && (
                      <Button
                        size="small"
                        variant="contained"
                        disabled={generatingInvoiceId === appointment.id}
                        onClick={() => handleGenerateInvoice(appointment)}
                        sx={{ ml: 0.5, whiteSpace: 'nowrap' }}
                      >
                        {generatingInvoiceId === appointment.id ? 'Generating…' : 'Invoice'}
                      </Button>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={8} sx={{ borderBottom: 'none' }}>
                <EmptyState
                  dense
                  title="No appointments here yet"
                  description="New bookings will appear the moment a customer schedules one."
                />
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default AppointmentsTable;
